import { RealtimeConfig } from './config';

// One EventSource per tab, whatever the number of subscribers.
//
// There is deliberately NO periodic polling: the catch-up happens on connection
// events instead. Every reconnection (network blip, Cloud Run request timeout,
// tab coming back to the foreground) replays the callbacks once, which covers
// exactly the window during which we were not listening.

let config = null;
let source = null;
let handlers = new Map(); // requested topic -> Set<callback>
let grantedByRequested = {};
let hasOpenedOnce = false;
let retryDelay = RealtimeConfig.INITIAL_RETRY_DELAY_MS;
let reopenTimer = null;
let hiddenTimer = null;
let visibilityBound = false;

/**
 * Called once per app: the DMC and the TO do not store their token the same way
 * (impersonation), and EventSource cannot go through their axios instances.
 */
export const configureRealtime = (nextConfig) => {
  config = nextConfig;
};

const requestedTopics = () => Array.from(handlers.keys());

const fireAll = () => {
  handlers.forEach((callbacks) => {
    callbacks.forEach((callback) => callback());
  });
};

const closeStream = () => {
  if (source) {
    source.close();
    source = null;
  }
};

const scheduleReopen = (delay = retryDelay) => {
  clearTimeout(reopenTimer);
  reopenTimer = setTimeout(() => {
    open();
  }, delay);
};

const createSubscription = async () => {
  const response = await fetch(
    `${config.baseUrl}${RealtimeConfig.SUBSCRIPTIONS_PATH}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${config.getAccessToken()}`,
      },
      body: JSON.stringify({ topics: requestedTopics() }),
    },
  );

  if (!response.ok) {
    throw new Error(`Realtime subscription failed: ${response.status}`);
  }

  return response.json();
};

async function open() {
  if (!config || handlers.size === 0 || document.hidden) return;

  closeStream();

  let subscription;
  try {
    subscription = await createSubscription();
  } catch {
    retryDelay = Math.min(retryDelay * 2, RealtimeConfig.MAX_RETRY_DELAY_MS);
    scheduleReopen();
    return;
  }

  grantedByRequested = subscription.topics ?? {};

  // streamUrl may be absolute (dedicated realtime host) or relative to the API.
  const streamUrl = new URL(subscription.streamUrl, config.baseUrl);
  streamUrl.searchParams.set(
    RealtimeConfig.SUBSCRIPTION_QUERY_PARAM,
    subscription.subscriptionId,
  );

  source = new EventSource(streamUrl.toString());

  Object.entries(grantedByRequested).forEach(([requested, granted]) => {
    source.addEventListener(granted, (event) => {
      let payload = {};
      try {
        payload = JSON.parse(event.data);
      } catch {
        payload = {};
      }
      handlers.get(requested)?.forEach((callback) => callback(payload));
    });
  });

  source.onopen = () => {
    retryDelay = RealtimeConfig.INITIAL_RETRY_DELAY_MS;
    // Not on the very first open: the components have just loaded their data.
    if (hasOpenedOnce) fireAll();
    hasOpenedOnce = true;
  };

  source.onerror = () => {
    // CONNECTING: EventSource is retrying on its own, leave it alone.
    // CLOSED: the subscription expired or was refused, we need a new one.
    if (source && source.readyState === EventSource.CLOSED) {
      retryDelay = Math.min(retryDelay * 2, RealtimeConfig.MAX_RETRY_DELAY_MS);
      scheduleReopen();
    }
  };
}

const bindVisibility = () => {
  if (visibilityBound || typeof document === 'undefined') return;
  visibilityBound = true;

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearTimeout(hiddenTimer);
      hiddenTimer = setTimeout(
        closeStream,
        RealtimeConfig.HIDDEN_TAB_CLOSE_DELAY_MS,
      );
      return;
    }

    clearTimeout(hiddenTimer);
    if (!source && handlers.size > 0) {
      scheduleReopen(0);
    }
  });
};

/**
 * The granted topics are sealed into the subscription, so adding one means a new
 * subscription and a new stream. Debounced so a burst of mounts costs one.
 */
export const subscribe = (topic, callback) => {
  bindVisibility();

  const isNewTopic = !handlers.has(topic);
  const callbacks = handlers.get(topic) ?? new Set();
  callbacks.add(callback);
  handlers.set(topic, callbacks);

  if (isNewTopic) {
    scheduleReopen(RealtimeConfig.RECONNECT_DEBOUNCE_MS);
  }

  return () => {
    callbacks.delete(callback);
    if (callbacks.size > 0) return;

    handlers.delete(topic);
    if (handlers.size === 0) {
      clearTimeout(reopenTimer);
      closeStream();
      hasOpenedOnce = false;
    } else {
      scheduleReopen(RealtimeConfig.RECONNECT_DEBOUNCE_MS);
    }
  };
};

/** Test seam: drops the connection and every subscriber. */
export const resetRealtime = () => {
  clearTimeout(reopenTimer);
  clearTimeout(hiddenTimer);
  closeStream();
  handlers = new Map();
  grantedByRequested = {};
  hasOpenedOnce = false;
  retryDelay = RealtimeConfig.INITIAL_RETRY_DELAY_MS;
};
